import type { AdSet } from 'app/services/facebook'
import { Disclosure } from '@headlessui/react'
import { ArrowPathIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import { useNavigate, useParams } from 'react-router-dom'
import classNames from 'classnames'
import { Xmark } from 'iconoir-react'
import { useEffect, useState } from 'react'
import { graphql } from 'app/generated/gql'
import ComboMultiSelect from 'app/components/ui/combo/combo-multi-select'
import { useConfirmation } from '../ConfirmationContext'
import Spinner from '../Spinner'
import { formatDate } from 'app/date.utils'
import type { LocationType } from 'app/types/entities'
import { gqlQuery } from 'app/lib/graphqlClient'
import { notifyError } from 'app/services/notify.client'
import { OptionType } from 'types/OptionType'
import { useLocationStore } from 'app/stores/StoreContext'

const FACEBOOK_AD_SETS_QUERY = graphql(`
  query FacebookAdSets($refresh: Boolean) {
    integrationOptions {
      facebookAdSets(refresh: $refresh) {
        id
        name
      }
    }
  }
`)

export default function FacebookAdsCard({
  onChange,
  isFetcherRunning,
  startOpen = false,
}: {
  onChange: (adSetIds: string[]) => void
  isFetcherRunning: boolean
  startOpen?: boolean
}) {
  // item={{ label: `Facebook Ads`, name: `facebookCombo` }}
  // integration={{
  //   options: facebookOptions,
  //   defaultOptions: facebookDefaultOptions ? facebookDefaultOptions : undefined,
  // }}

  const navigate = useNavigate()
  const { locationId } = useParams()
  const locationStore = useLocationStore()
  const [confirm] = useConfirmation()
  const [location, setLocation] = useState<LocationType | undefined>()

  useEffect(() => {
    if (!locationId) return
    locationStore
      .fetchOne(locationId)
      .then((result) => {
        const data = result.data as any
        const loc = data?.location ?? data?.hits?.[0] ?? data
        if (loc) setLocation(loc as LocationType)
      })
      .catch(() => {})
  }, [locationId])

  const [isLoading, setIsLoading] = useState(false)

  // FACEBOOK data read/write
  const [fetcherLoading, setFetcherLoading] = useState(false)
  const [values, setValues] = useState<OptionType[]>([])
  const [options, setOptions] = useState<OptionType[]>([])

  const loadFacebookData = (refresh = false) => {
    if (fetcherLoading) return
    setFetcherLoading(true)
    gqlQuery<{ integrationOptions: { facebookAdSets: Array<{ id: string; name: string }> } }>(FACEBOOK_AD_SETS_QUERY, {
      refresh: refresh || undefined,
    })
      .then(({ data, error }) => {
        if (data?.integrationOptions?.facebookAdSets) {
          setOptions(
            data.integrationOptions.facebookAdSets
              .filter((ix) => ix)
              .map((adSet) => ({
                id: adSet.id,
                name: adSet.name,
                value: adSet.id,
              })),
          )
        } else if (error) {
          notifyError(
            `failed to load facebook adSets: ${error.message}. This is likely due to API throttling from facebook and should work again in 5-10 minutes.`,
          )
        }
      })
      .finally(() => setFetcherLoading(false))
  }

  useEffect(() => {
    loadFacebookData()
  }, [])

  useEffect(() => {
    if (options.length && location) {
      const adSetIdsFromV2 =
        location.facebookAdsV2?.filter((entry) => entry.adSetId).map((entry) => entry.adSetId!) ?? []
      const legacyIds = location.facebookAdSetIds ?? []
      const allAdSetIds = adSetIdsFromV2.length ? adSetIdsFromV2 : legacyIds
      setValues(allAdSetIds.length ? options.filter((option) => allAdSetIds.includes(option.id)) : [])
    }
  }, [options, location?.facebookAdSetIds, location?.facebookAdsV2])

  useEffect(() => {
    if (!isFetcherRunning) {
      setIsLoading(false)
    }
  }, [isFetcherRunning])

  const onRemove = () => {
    return confirm({
      title: `Remove Facebook Ads Integration`,
      description: `Removing this integration will also remove all associated data. This may result in contacts being removed. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      window
        .fetch(`/api/integrations/remove`, {
          method: `POST`,
          credentials: `include`,
          headers: { 'Content-Type': `application/json` },
          body: JSON.stringify({ integration: `facebook`, locationId: location?.id! }),
        })
        .then(() => {
          navigate(0)
        }),
    )
  }

  return (
    <div className="w-full rounded-lg border border-default shadow-elevation-1">
      <Disclosure defaultOpen={startOpen}>
        {({ open }) => (
          <>
            <Disclosure.Button className="flex h-16 w-full items-center justify-between px-4">
              <div className="flex items-center justify-between">
                <img className="h-8 w-auto" src="/public/meta_icon.png" alt="Facebook Ads" />
                <div className="ml-4 text-heading-sm text-strong">Facebook Ads</div>
              </div>
              <div className="flex">
                <div className="flex items-center">
                  <div className="flex items-center justify-start">
                    {!!(location?.facebookAdSetIds?.length || location?.facebookAdsV2?.length) && (
                      <span className="inline-flex items-center rounded-full bg-green-200 px-3 py-0.5 text-sm font-medium text-green-600">
                        Active
                      </span>
                    )}
                  </div>
                </div>
                <div className="ml-8 flex items-center">
                  <p className="mr-2 text-ui-sm text-strong">Details</p>
                  {open ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                </div>
              </div>
            </Disclosure.Button>
            <Disclosure.Panel className="relative border-t border-default pt-6">
              {isLoading && (
                <>
                  <div className="absolute top-0 z-10 flex h-full w-full items-center justify-center bg-surface bg-opacity-80">
                    <Spinner size={8} />
                  </div>
                </>
              )}
              <div className="px-4 pb-6">
                <h6 className="text-strong">Facebook Ad Set</h6>
                <p className="mt-6 text-body-sm text-strong">Facebook Ad Set search</p>
                <div className="mt-1.5 flex items-center">
                  <div className="w-2/3">
                    <ComboMultiSelect
                      name="facebookCombo"
                      placeholder="Select"
                      options={options}
                      values={values}
                      loading={fetcherLoading}
                      onChange={setValues}
                    />
                    {values && values.length > 0 && (
                      <div className="mt-2 flex gap-2 rounded-lg border border-default p-4">
                        <div className="inline-flex flex-wrap gap-2">
                          {values?.map((option) => (
                            <span
                              key={option.id}
                              className="flex items-baseline justify-center gap-3 rounded border border-default bg-surface-subtle p-2 text-body-sm text-strong"
                            >
                              {option.name}

                              <Xmark
                                className="h-3 w-3 hover:cursor-pointer"
                                onClick={() => {
                                  const options = values.filter((prevOption) => prevOption.id !== option.id)
                                  setValues(options)
                                }}
                              />
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <ArrowPathIcon
                      className={classNames(`ml-2 h-5 w-5 cursor-pointer`, fetcherLoading && `animate-spin`)}
                      onClick={() => !fetcherLoading && loadFacebookData(true)}
                    />
                  </div>
                </div>
              </div>
              <div className="border-t border-default px-4 py-6">
                <p className="text-ui-sm text-strong">Last Webhook Received</p>
                <p className="mt-2 text-body-sm text-strong">
                  {formatDate({ value: location?.facebookSpendUpdateDate, dateOnly: false })}
                </p>
              </div>
              <div className="border-t border-default px-4 py-4">
                <div className="flex items-center justify-between">
                  <button
                    disabled={
                      !(location?.facebookAdSetIds?.length || location?.facebookAdsV2?.length) ? true : undefined
                    }
                    className="btn btn-danger btn-sm"
                    onClick={() => onRemove()}
                  >
                    Remove
                  </button>

                  <button
                    className="btn btn-primary btn-sm ml-4"
                    disabled={isFetcherRunning}
                    onClick={() => {
                      onChange(values?.map((v) => v.id))
                    }}
                  >
                    {isFetcherRunning ? <Spinner size={4} /> : `Save`}
                  </button>
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  )
}
